import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  X,
  ShoppingCart,
  ChefHat,
  Heart,
  Car,
  Check,
  Sparkles,
  PartyPopper,
  Clock,
  Brush,
  BookOpen,
  MessageCircle,
} from "lucide-react";
import request from "../../utils/request";
import PhoneDemo from "../PhoneDemo";
import UNSPLASH from "../unsplashImages";
import { useSelector } from "react-redux";

const DISMISS_KEY = "concierge_promo_dismissed";
const PHONE_SCALE = 200 / PhoneDemo.WIDTH;

const CONCIERGE_SERVICES = [
  { id: "private-chef", key: "private_chef", icon: ChefHat, displayName: "Private Chef" },
  { id: "massage", key: "massage", icon: Heart, displayName: "In-Home Massage" },
  { id: "airport-pickup", key: "airport_ride", icon: Car, displayName: "Airport Transfer" },
  { id: "arrival-event", key: "arrival_event", icon: PartyPopper, displayName: "Event Setup" },
  { id: "early-checkin", key: "early_checkin", icon: Clock, displayName: "Early Check-In" },
  { id: "mid-stay-cleaning", key: "mid_stay_cleaning", icon: Brush, displayName: "Mid-Stay Cleaning" },
];

export default function ConciergePromoCard({ hostId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );
  const [toggles, setToggles] = useState({});

  const hostDetails = useSelector((state) => state.hostDetails?.Info);
  const hostLogo = hostDetails?.logoUrl || hostDetails?.profilePicture || null;

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(toggles).forEach(([id, on]) => {
      if (on) set.add(id);
    });
    return set;
  }, [toggles]);

  const handleToggle = useCallback((id) => {
    setToggles((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    if (!hostId || dismissed) return;
    request
      .get(`/hosts/${hostId}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setLoading(false));
  }, [hostId, dismissed]);

  if (dismissed || loading || status?.conciergeEnabled) return null;

  const enabledCount = Object.values(toggles).filter(Boolean).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative overflow-hidden rounded-3xl mb-2"
      style={{
        background: `linear-gradient(135deg, rgba(255,255,255,0.97) 55%, rgba(254,242,242,0.92) 100%), url(${UNSPLASH.lifestyle.luxuryInterior})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <button
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, "true");
          setDismissed(true);
        }}
        className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/80 border border-gray-200 flex items-center justify-center hover:bg-white transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} className="text-gray-500" />
      </button>

      <div className="flex flex-col laptop:flex-row items-stretch">
        {/* Left: Copy + interactive toggles */}
        <div className="flex-1 p-6 laptop:p-8">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-500 mb-3">
            Bellhopt Concierge
          </p>

          <h2
            className="text-2xl laptop:text-[28px] font-extrabold text-gray-900 mb-3"
            style={{ lineHeight: 1.1, letterSpacing: -0.5 }}
          >
            Give your guests a<br />
            5-star experience.
          </h2>

          {/* Free section */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
            <div className="flex items-center justify-between gap-2.5 mb-2.5">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                Free with every Bellhopt account
              </p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wide">
                <Check size={8} strokeWidth={3} />
                Free
              </span>
            </div>
            <div className="space-y-1.5">
              <IncludedRow
                icon={<ShoppingCart size={13} className="text-emerald-600" strokeWidth={2} />}
                label="Pre-Arrival Grocery Delivery"
              />
              <IncludedRow
                icon={<BookOpen size={13} className="text-emerald-600" strokeWidth={2} />}
                label="Branded Guest Guidebook"
              />
              <IncludedRow
                icon={<MessageCircle size={13} className="text-emerald-600" strokeWidth={2} />}
                label="AI Guest Concierge"
              />
            </div>
          </div>

          {/* Premium services with toggles */}
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-2">
            Premium services — toggle to preview
          </p>
          <div className="space-y-1.5 mb-5">
            {CONCIERGE_SERVICES.map((svc) => {
              const on = !!toggles[svc.id];
              return (
                <div
                  key={svc.id}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                    on
                      ? "bg-white border-primary-200 shadow-sm"
                      : "bg-white/60 border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      on ? "bg-primary-500 text-white" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <svc.icon size={13} strokeWidth={2} />
                  </div>
                  <span className={`flex-1 text-[13px] font-medium ${on ? "text-gray-900" : "text-gray-500"}`}>
                    {svc.displayName}
                  </span>
                  <button
                    onClick={() => handleToggle(svc.id)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      on ? "bg-primary-500" : "bg-gray-300"
                    }`}
                  >
                    <motion.span
                      className="inline-block h-4 w-4 rounded-full bg-white shadow-sm"
                      animate={{ x: on ? 17 : 2 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/concierge-subscription")}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt"
            >
              {enabledCount > 0 ? `Explore ${enabledCount} Services` : "Explore Concierge"}
              <ArrowRight size={14} strokeWidth={2.5} />
            </button>
            <span className="hidden laptop:inline-flex items-center gap-1.5 text-xs text-gray-400">
              <Check size={12} className="text-emerald-500" />
              Cancel anytime
            </span>
          </div>
        </div>

        {/* Right: Phone preview */}
        <div className="hidden laptop:flex items-center justify-center px-6 py-6 flex-shrink-0">
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              width: 210,
              height: 428,
              borderRadius: 36,
              background: "#000",
              border: "1px solid rgba(0,0,0,0.1)",
              position: "relative",
              boxShadow: "0 25px 80px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <div style={{
              position: "absolute", top: 7, left: "50%", transform: "translateX(-50%)",
              width: 64, height: 18, borderRadius: 9, background: "#000", zIndex: 20,
            }} />
            <div style={{
              position: "absolute", top: 4, left: 4, right: 4, bottom: 4,
              borderRadius: 32, overflow: "hidden", background: "#fff",
            }}>
              <div style={{
                width: PhoneDemo.WIDTH, height: PhoneDemo.HEIGHT,
                transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left",
              }}>
                <PhoneDemo enabledServices={enabledServices} hostLogo={hostLogo} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

function IncludedRow({ icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <p className="text-[13px] font-semibold text-gray-900 truncate">{label}</p>
    </div>
  );
}
